import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  Box,
  CompassColor,
  Button,
  Text,
  useToast,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from "@noom/wax-component-library";
import { useLogger } from "@noom/hive-analytics";
import React, { useEffect } from "react";

import { MedShipmentStatus } from "gql/graphql";

import { ShippingAddressSection } from "../components/shipping/ShippingAddressSection";
import { UpcomingShipmentSection } from "../components/shipping/UpcomingShipmentSection";
import { useAuthCredentials } from "../hooks/useAuthCredentials";
import { usePatient } from "../hooks/usePatient";
import {
  type ShippingDetailsData,
  useShippingDetails,
} from "../hooks/useShippingDetails";
import {
  isMedNextShipment,
  isOtcNextShipment,
  type MedNextShipment,
  type OtcNextShipment,
} from "../modules/shipping/nextShipmentGuards";
import { MixpanelTypes } from "../common/types";
import { Loading } from "../components/Loader";
import { getBuyflowUrl, AddressChangeOrigin } from "../utils/urlUtils";
import {
  formatLocalDateDeliveryWindow,
  formatShippingLocalDate,
} from "../utils/dateUtils";
import greenCheckIcon from "../image_assets/green-check.svg";
import medIcon from "../image_assets/med.svg";
import informationIconBlue from "../image_assets/informationIconBlue.svg";

const InformationIcon = styled.img`
  max-width: 20px;
  max-height: 20px;
`;

const ClickableIcon = styled(InformationIcon)`
  cursor: pointer;
`;

const PopoverContentStyled = styled(PopoverContent)`
  width: 300px;
  max-width: calc(100vw - 32px);
  background-color: ${CompassColor.black};
  border: 1px solid ${CompassColor.black};
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    width: calc(100vw - 48px);
    max-width: 300px;
  }

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    max-width: none;
  }

  @media (max-width: 375px) {
    width: calc(100vw - 24px);
  }
`;

const PopoverBodyStyled = styled(PopoverBody)`
  padding: 16px;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    padding: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    font-size: 14px;
    line-height: 1.4;
  }

  @media (max-width: 375px) {
    padding: 10px;
  }
`;

const PopoverArrowStyled = styled(PopoverArrow)`
  background-color: ${CompassColor.black};
  border-color: ${CompassColor.black};
`;

const UpdateButton = styled(Button)`
  border-radius: 9999px;
`;

const ToggleTip: React.FC = () => {
  return (
    <Popover placement="bottom" trigger="click" closeOnBlur={true}>
      <PopoverTrigger>
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          background="transparent"
          border="none"
          padding="0"
          cursor="pointer"
          marginLeft="8px"
          aria-label="Information"
          data-cy="refill-info-button"
        >
          <ClickableIcon src={informationIconBlue} alt="information icon" />
        </Box>
      </PopoverTrigger>
      <PopoverContentStyled data-cy="refill-tooltip">
        <PopoverArrowStyled
          bg={CompassColor.black}
          borderColor={CompassColor.black}
        />
        <PopoverBodyStyled>
          <Text
            fontSize="15px"
            color={CompassColor.offWhite}
            lineHeight="1.5"
            textAlign="center"
            data-cy="refill-tooltip-text"
          >
            This is the day we will send your prescription to the pharmacy.
            Prescriptions typically ship 3-7 business days later.
          </Text>
        </PopoverBodyStyled>
      </PopoverContentStyled>
    </Popover>
  );
};

const formatShipmentDate = (date?: string | null): string =>
  date ? formatShippingLocalDate(date) : "Unable to retrieve date";

const formatShipmentDeliveryWindow = (
  range?: { startDate: string; endDate: string } | null
): string =>
  range ? formatLocalDateDeliveryWindow(range) : "Unable to retrieve date";

const shouldShowGreenCheck = (shipmentStatus: MedShipmentStatus): boolean => {
  return shipmentStatus === MedShipmentStatus.Ordered;
};

type MedRefillSectionProps = {
  shipment: MedNextShipment;
  onUpdateRefill: () => void;
};

const MedRefillSection: React.FC<MedRefillSectionProps> = ({
  shipment,
  onUpdateRefill,
}) => {
  const nextRefillDate = formatShipmentDate(shipment.fillDate);
  const showGreenCheck = shouldShowGreenCheck(shipment.shipmentStatus);

  return (
    <UpcomingShipmentSection
      title="Next refill"
      titleDataCy="next-refill-title"
      sectionDataCy="next-refill-section"
      headerAction={
        <UpdateButton
          size="sm"
          color={CompassColor.black}
          colorScheme="secondary"
          className="button"
          type="button"
          variant="outline"
          onClick={onUpdateRefill}
          data-cy="refill-update-button"
          isDisabled={!shipment.canUpdateFillDate}
        >
          Update
        </UpdateButton>
      }
      statusIconSrc={showGreenCheck ? greenCheckIcon : medIcon}
      statusIconAlt={showGreenCheck ? "Medication filled" : "Next refill date"}
      statusIconDataCy="refill-icon"
      statusLabel={
        showGreenCheck ? `Refilled  ${nextRefillDate}` : "Next refill date"
      }
      statusLabelDataCy="refill-status-text"
      statusLabelAccessory={!showGreenCheck ? <ToggleTip /> : undefined}
      dateText={showGreenCheck ? "" : nextRefillDate}
      dateTextDataCy="refill-date-text"
      deliveryWindow={formatShipmentDeliveryWindow(
        shipment.estimatedDeliveryWindow
      )}
    />
  );
};

type OtcNextShipmentSectionProps = {
  shipment: OtcNextShipment;
};

const OtcNextShipmentSection: React.FC<OtcNextShipmentSectionProps> = ({
  shipment,
}) => (
  <UpcomingShipmentSection
    title="Next shipment"
    titleDataCy="next-shipment-title"
    sectionDataCy="next-shipment-section"
    statusIconSrc={medIcon}
    statusIconAlt="Next shipment date"
    statusIconDataCy="shipment-icon"
    statusLabel="Next shipment date"
    statusLabelDataCy="shipment-product-name"
    dateText={formatShipmentDate(shipment.shipDate)}
    dateTextDataCy="shipment-date-text"
    deliveryWindow={formatShipmentDeliveryWindow(
      shipment.estimatedDeliveryWindow
    )}
  />
);

export type ShippingAndRefillsPageViewProps = {
  shippingDetails: ShippingDetailsData | null;
  loading: boolean;
  error?: Error;
  onUpdateShipping: () => void;
  onUpdateRefill: () => void;
};

export const ShippingAndRefillsPageView = ({
  shippingDetails,
  loading,
  error,
  onUpdateShipping,
  onUpdateRefill,
}: ShippingAndRefillsPageViewProps) => {
  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <>
        <Box padding="24px" maxWidth="800px" margin="0 auto" textAlign="center">
          <p data-cy="shipping-error">Error loading shipping information</p>
        </Box>
      </>
    );
  }

  const firstNextShipment = shippingDetails?.nextShipments[0] ?? null;
  const showRefillSection = shippingDetails?.showRefillSection === true;
  const addressInfo = shippingDetails?.address;

  let upcomingShipmentSection: React.ReactNode = null;
  if (firstNextShipment) {
    if (isOtcNextShipment(firstNextShipment)) {
      upcomingShipmentSection = (
        <OtcNextShipmentSection shipment={firstNextShipment} />
      );
    } else if (isMedNextShipment(firstNextShipment) && showRefillSection) {
      upcomingShipmentSection = (
        <MedRefillSection
          shipment={firstNextShipment}
          onUpdateRefill={onUpdateRefill}
        />
      );
    }
  }

  return (
    <>
      <Box padding="24px" maxWidth="800px" margin="0 auto">
        {upcomingShipmentSection}

        <ShippingAddressSection
          addressInfo={addressInfo}
          onUpdate={onUpdateShipping}
        />
      </Box>
    </>
  );
};

export const ShippingAndRefillsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const patient = usePatient();
  const { accessCode } = useAuthCredentials();
  const { shippingDetails, loading, error, refetch } = useShippingDetails();
  const toast = useToast();
  const mixpanelLogger = useLogger();

  useEffect(() => {
    mixpanelLogger.log(MixpanelTypes.OnShippingAndRefillsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const toastParam = searchParams.get("toastMsg");
    if (toastParam) {
      const decodedMessage = decodeURIComponent(toastParam);
      toast({
        title: decodedMessage,
        status: "success",
        duration: null,
        isClosable: true,
        position: "top",
      });
      // Clear the toast param without dropping userTreatmentId.
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("toastMsg");
      setSearchParams(nextParams, { replace: true });
      refetch();
    }
  }, [searchParams, setSearchParams, toast, refetch]);

  const handleUpdateShipping = () => {
    const userId = patient?.user_id ?? "";
    window.location.href = getBuyflowUrl(
      "changeAddress",
      accessCode ?? "",
      userId,
      AddressChangeOrigin.PRESCRIPTION_PAGE,
      "push",
      "Update shipping address",
      window.location.href
    );
  };

  const handleUpdateRefill = () => {
    const userTreatmentId = searchParams.get("userTreatmentId");
    navigate(
      userTreatmentId
        ? `/update-refill?userTreatmentId=${encodeURIComponent(userTreatmentId)}`
        : "/update-refill"
    );
  };

  return (
    <ShippingAndRefillsPageView
      shippingDetails={shippingDetails}
      loading={loading}
      error={error}
      onUpdateShipping={handleUpdateShipping}
      onUpdateRefill={handleUpdateRefill}
    />
  );
};
